import * as React from "react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { cn } from "@/lib/utils";
import type { RenewalFamily } from "@/types/renewals";

import { LEGAL_REVIEW_LABELS, SPEND_CATEGORY_LABELS, statusLabel, statusVariant } from "../constants";
import { compactGbp, actionLabel } from "../renewal-format";

interface Props {
  family: RenewalFamily;
  ownerLabel: string | null;
}

const urgencyClass = (days: number | null) => {
  if (days === null) return "text-gray-500";
  if (days < 0) return "text-red-700";
  if (days <= 30) return "text-orange-700";
  return "text-gray-700";
};

// Memoised: the board re-renders on every filter keystroke, most cards stay unchanged.
export const RenewalCard = React.memo(({ family, ownerLabel }: Props) => {
  const { formatDate } = useDateFormatter();
  const value = compactGbp(family.total_contract_value_gbp);
  // The contract's own amount, shown under the GBP value when it was converted.
  const native =
    family.total_contract_value !== null && family.currency_code && family.currency_code !== "GBP"
      ? `${family.currency_code} ${Number(family.total_contract_value).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
      : null;

  return (
    <Link
      to={`/renewal-centre/${encodeURIComponent(family.relation_id)}`}
      className="block rounded-lg border border-[#e4ddd5] bg-[#fffdfb] p-3 shadow-sm transition-colors hover:border-desert-sand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-desert-sand-500"
      aria-label={`${family.supplier_name} – ${family.family_name}`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 break-words text-sm font-semibold leading-snug text-[#111b35]">{family.family_name}</p>
        {family.procurement_status ? (
          <Badge variant={statusVariant(family.procurement_status)} className="shrink-0 uppercase tracking-wide">
            {statusLabel(family.procurement_status)}
          </Badge>
        ) : null}
      </div>
      <p className="mt-0.5 break-words text-xs text-gray-600">{family.supplier_name}</p>
      {family.contract_title ? <p className="text-xs text-gray-400">{family.contract_title}</p> : null}
      {ownerLabel ? <p className="mt-1 text-xs text-gray-500">{ownerLabel}</p> : null}
      <dl className="mt-3 flex flex-col gap-2">
        {family.category_l2 ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">L2 category</dt>
            <dd className="mt-0.5">
              <Badge variant="outline" className="font-medium text-gray-600">
                {family.category_l2}
              </Badge>
            </dd>
          </div>
        ) : null}
        {family.category_l3 ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">L3 category</dt>
            <dd className="mt-0.5">
              <Badge variant="outline" className="font-medium text-gray-600">
                {family.category_l3}
              </Badge>
            </dd>
          </div>
        ) : null}
        {family.termination_type ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Termination type</dt>
            <dd className="mt-0.5">
              <Badge variant="outline" className="border-sky-200 bg-sky-50 font-medium text-sky-800">
                {family.termination_type}
              </Badge>
            </dd>
          </div>
        ) : null}
        {family.spend_category ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Spend category</dt>
            <dd className="mt-0.5">
              <Badge variant="outline" className="border-violet-200 bg-violet-50 font-medium text-violet-800">
                {SPEND_CATEGORY_LABELS[family.spend_category]}
              </Badge>
            </dd>
          </div>
        ) : null}
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">Legal review</dt>
          <dd className="mt-0.5">
            <Badge variant="outline" className="font-medium text-gray-600">
              {family.legal_review_status ? LEGAL_REVIEW_LABELS[family.legal_review_status] : "Not set"}
            </Badge>
          </dd>
        </div>
      </dl>
      <div className="mt-3 flex items-end justify-between gap-2 border-t border-[#efe9e2] pt-2">
        <div>
          <p className={cn("text-xs font-semibold", urgencyClass(family.days_to_action))}>
            {actionLabel(family.days_to_action)}
          </p>
          {family.action_date ? (
            <p className="text-[11px] text-gray-500">Action {formatDate(family.action_date)}</p>
          ) : null}
          {family.term_end_date ? (
            <p className="text-[11px] text-gray-400">Term ends {formatDate(family.term_end_date)}</p>
          ) : null}
        </div>
        <div className="text-right">
          {value ? <p className="text-sm font-semibold tabular-nums text-[#111b35]">{value}</p> : null}
          {native ? <p className="text-[11px] tabular-nums text-gray-400">{native}</p> : null}
        </div>
      </div>
    </Link>
  );
});
RenewalCard.displayName = "RenewalCard";
